import { useEffect, useState } from 'react'

import PropTypes from 'prop-types'
import { Box } from '@material-ui/core'
import { Controller, FormContext, useForm } from 'react-hook-form'

import {
  Select as SelectComponent,
  DialogLayout,
  LoadingBox,
  OrganizationTypeAndSearchInput,
  UserTypeAndSearchInput,
} from 'components'

import { useSnackbar } from 'hooks'

import * as service from 'service'
import helpers from 'helpers'
import constants from 'constants/index'
import schema from './schema'

const OrganizationUsersForm = ({
  userId = '',
  organization = {},
  setOpen,
  open,
  refresh = () => {},
  isEdit = false,
  currentOrganizationUser,
  isUserCreation = true,
}) => {
  const [selectedOrganization, setSelectedOrganization] = useState({
    ...organization,
  })
  const [selectedUser, setSelectedUser] = useState({ id: userId })
  const [profiles, setProfiles] = useState([])
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)

  const snackbar = useSnackbar()
  const formMethods = useForm({
    validationSchema: schema(isEdit, isUserCreation),
    defaultValues: {
      organizationId: currentOrganizationUser?.organization || null,
      userId: currentOrganizationUser?.user || null,
      profileId: currentOrganizationUser?.profile?.id || '',
    },
  })

  const { control, handleSubmit, errors, setValue } = formMethods

  const onSubmit = async (data) => {
    const { profileId, organizationId } = data

    try {
      if (isEdit) {
        await service.backoffice.organizationUsers.update({
          organizationId: currentOrganizationUser?.organization?.id,
          id: currentOrganizationUser?.id,
          profileId: profileId,
        })
      } else {
        await service.backoffice.organizationUsers.create({
          id: selectedOrganization?.id || organizationId,
          userId: selectedUser?.id,
          organizationUser: {
            profileId: profileId,
          },
        })
      }

      refresh()

      snackbar.open({
        message: isEdit
          ? `Perfil do usuário no Grupo Advisor alterado!`
          : `Usuário vinculado ao Grupo Advisor.`,
        variant: 'success',
      })
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setOpen(false)
    }
  }

  const handleChangeOrganizationOrUser = () => {
    setValue('profileId', '')
  }

  useEffect(() => {
    const loadProfiles = async () => {
      if (!selectedOrganization?.id) {
        setProfiles([...constants.profile.DEFAULT_DPO_PROFILES_OPTIONS])
        return
      }

      setIsLoadingProfile(true)
      try {
        const response = await service.backoffice.advisorProfiles.get({
          perPage: 100000,
          organizationId: selectedOrganization?.id,
          status: true,
        })

        if (selectedOrganization?.kind === constants.organizations.DPO_KIND) {
          setProfiles([
            ...constants.profile.DEFAULT_DPO_PROFILES_OPTIONS,
            ...response?.data?.profiles,
          ])
        } else {
          setProfiles([
            ...constants.profile.DEFAULT_OBSERVER_PROFILES_OPTIONS,
            ...response?.data?.profiles,
          ])
        }
      } catch (error) {
        console.error('Falha ao carregar perfis:', error)
        setProfiles(constants.profile.DEFAULT_DPO_PROFILES_OPTIONS)
      } finally {
        setIsLoadingProfile(false)
      }
    }

    loadProfiles()
  }, [selectedOrganization])

  return (
    <FormContext {...formMethods}>
      <DialogLayout
        title={
          isEdit
            ? 'Editar vínculo de Grupo Advisor e usuário'
            : 'Vincular Grupo Advisor ao usuário'
        }
        open={open}
        setOpen={setOpen}
        confirmTextButton="Salvar"
        handleSubmit={handleSubmit(onSubmit)}
      >
        <Box width={500}>
          {!isEdit && (
            <Box pb={2}>
              {isUserCreation ? (
                <OrganizationTypeAndSearchInput
                  setOrganization={setSelectedOrganization}
                  handleChangeOrganization={handleChangeOrganizationOrUser}
                />
              ) : (
                <UserTypeAndSearchInput
                  setUserId={setSelectedUser}
                  handleChangeUser={handleChangeOrganizationOrUser}
                />
              )}
            </Box>
          )}
          <Box pb={1}>
            {!isLoadingProfile ? (
              <Controller
                control={control}
                name="profileId"
                mode="onChange"
                as={
                  <SelectComponent
                    disabled={
                      !selectedOrganization?.id && isUserCreation && !isEdit
                    }
                    label="Perfil de usuário"
                    items={profiles || []}
                    error={!!errors.profileId}
                    loading={isLoadingProfile}
                    helperText={errors?.profileId?.message}
                  />
                }
              />
            ) : (
              <LoadingBox />
            )}
          </Box>
        </Box>
      </DialogLayout>
    </FormContext>
  )
}

OrganizationUsersForm.propTypes = {
  userId: PropTypes.number,
  setOpen: PropTypes.func,
  open: PropTypes.bool,
  refresh: PropTypes.func,
  isEdit: PropTypes.bool,
  currentOrganizationUser: PropTypes.object,
  isUserCreation: PropTypes.bool,
}

export default OrganizationUsersForm
